import React, { useRef, useCallback, useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';
import { useTranslation } from 'react-i18next';
import ColorPicker from '../../../artboard/ColorPicker';

import { Input, InputNumber, Tooltip, Switch, Icon, Select, message } from 'antd';
import { Container, ColumnSection, FieldTitle, RowSection,
  PaddingOption, BlockColor } from '../../../components/ComplexBlockSettings/styles';
import { AllSidesGrid, HorizontalBorderGrid, VerticalBorderGrid } from './GridLineIcons';
import { DYNAMIC_TABLE_SETTINGS_SIDEBAR } from './translationValues';
import { BLOCK_SETTINGS_OPTIONS } from '../translationValues';
import { cancellablePromise, useCancellablePromise } from '../cancellablePromise';
import { ColorPickerContainer, EmptyMappingNote } from '../styles';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { TEXT_DEBOUNCE_TIME_OUT, dummyCallback } from '../../../utils/complexDocumentConstants';
import { measurementConversion } from '../../../utils/javascript';
import { BorderedWhiteQuillRenderer } from './BorderedWhiteQuillRenderer';
import VariableMentionPopup from '../../../containers/EmailDesignPage/EmailBuilder/VariableMentionPopup';
import regex from '../../../utils/regex';

const NAMESPACES = ['DYNAMIC_TABLE_SETTINGS_SIDEBAR', 'BLOCK_SETTINGS_OPTIONS'];
const { Option } = Select;

const DynamicTableSettings = (props) => {
  const { t } = useTranslation(NAMESPACES);
  const fieldNameRef = useRef(null);
  const repeatVariableRef = useRef(null);
  const forwardRef = useRef(null);
  const previousRepeatValueRef = useRef(null);

  const [measurement, setMeasurement] = useState('px');
  const [picker, setPicker] = useState(null);
  const [textEditable, setTextEditable] = useState(true);
  const [disableVariableName, setDisableVariableName] = useState(false);
  const [canUpdateRandomKey, setCanUpdateRandomKey] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [popupPosition, setPopupPosition] = useState(null);

  const { itemUUID, parentUUID, onClose, selectCellFormat, selectedColumn } = props;

  const customSelector = useCallback(store => store[itemUUID], []);
  const [dynamicTableBlock, methods] = useDocumentContext(customSelector, `DynamicTableSettings-${itemUUID}`);

  const headerPadding = dynamicTableBlock.style.tableProperties.showHeader ? (dynamicTableBlock.contents[0]?.headerContent?.style?.padding || {}) : {};
  const headerPaddingChanges = useRef(headerPadding);
  const [localHeaderPadding, setLocalHeaderPadding] = useState(headerPadding);

  const tableNameRef = useRef(dynamicTableBlock.variableContract[0]?.name);

  const cellPadding = dynamicTableBlock.contents[0]?.content?.style?.padding;
  const cellPaddingChanges = useRef(cellPadding);
  const [localCellPadding, setLocalCellPadding] = useState(cellPadding);

  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(customVarSelector, `DynamicTableDesign-${itemUUID}`);

  const isEmailSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE], []);
  const [isEmailTemplate] = useDocumentContext(isEmailSelector, `DynamicTableDesign-${itemUUID}`);

  const { addCancellablePromise, removePendingPromise, clearPendingPromises } = useCancellablePromise();
  const {
    addCancellablePromise: changeHeaderPadding,
    removePendingPromise: removePendingChange,
    clearPendingPromises: clearPreviousChanges } = useCancellablePromise();

  const columnUUID = (selectedColumn ?? null) !== null ? dynamicTableBlock.contents[selectedColumn].uuid : undefined

  useEffect(() => {
    forwardRef.current = true;
  }, [itemUUID, dynamicTableBlock]);

  useEffect(() => {
    const newHeaderPadding = dynamicTableBlock.contents[0]?.headerContent?.style?.padding;
    const newLocalHeaderPadding = {
      top: measurement === 'px' ? newHeaderPadding.top : measurementConversion(newHeaderPadding.top, measurement),
      right: measurement === 'px' ? newHeaderPadding.right : measurementConversion(newHeaderPadding.right, measurement),
      bottom: measurement === 'px' ? newHeaderPadding.bottom : measurementConversion(newHeaderPadding.bottom, measurement),
      left: measurement === 'px' ? newHeaderPadding.left : measurementConversion(newHeaderPadding.left, measurement)
    };
    setLocalHeaderPadding(newLocalHeaderPadding);
    headerPaddingChanges.current = newLocalHeaderPadding;
    const newCellPadding = dynamicTableBlock.contents[0]?.content?.style?.padding;
    const newLocalPadding = {
      top: measurement === 'px' ? newCellPadding.top : measurementConversion(newCellPadding.top, measurement),
      right: measurement === 'px' ? newCellPadding.right : measurementConversion(newCellPadding.right, measurement),
      bottom: measurement === 'px' ? newCellPadding.bottom : measurementConversion(newCellPadding.bottom, measurement),
      left: measurement === 'px' ? newCellPadding.left : measurementConversion(newCellPadding.left, measurement)
    };
    setLocalCellPadding(newLocalPadding);
    cellPaddingChanges.current = newCellPadding;
  }, [dynamicTableBlock, measurement]);

  const updateBlockVariableName = useCallback((value) => {
    methods.updateBlock('repetitive', itemUUID, {
      repeat: true,
      variableName: value,
      flowUUID: parentUUID,
      isTableName: true
    });
  }, [itemUUID, parentUUID]);

  const debouncedUpdateBlockVariableName = useMemo(() => {
    return _.debounce(updateBlockVariableName, TEXT_DEBOUNCE_TIME_OUT);
  }, [updateBlockVariableName]);

  useEffect(() => {
    if (forwardRef.current === true && fieldNameRef.current !== null) {
      if (
        dynamicTableBlock?.variableContract &&
        dynamicTableBlock.variableContract[0].type === 'object'
      ) {
        const tableName = (dynamicTableBlock.variableContract[0].isArray
          ? dynamicTableBlock.variableContract[0].content.find(variable => variable.type === 'object')?.name
          : dynamicTableBlock.variableContract[0].name) || '';
        tableNameRef.current = tableName;
        fieldNameRef.current.setState({ value: tableName });
        const hasRepeatVariable = dynamicTableBlock.variableContract[0].isArray
            ? dynamicTableBlock.variableContract[0].content.find(variable => variable.type === 'object')?.content?.find(variable => variable.type === 'object')
            : dynamicTableBlock.variableContract[0]?.content?.find(variable => variable.type === 'object');
        setDisableVariableName(hasRepeatVariable);
      }
      forwardRef.current = false;
    }
  }, [dynamicTableBlock]);

  useEffect(() => {
    if (selectedColumn !== null) {
      setSearchTerm(`${dynamicTableBlock.contents[selectedColumn]?.repeatVariable ? `${dynamicTableBlock.contents[selectedColumn]?.repeatVariable}` : ''}`);
      setPopupPosition(null);
    }
    if (repeatVariableRef.current) {
      repeatVariableRef.current.blur();
    }
  }, [dynamicTableBlock, selectedColumn]);

  const showBackgroundColorPicker = useCallback((e) => {
    e.persist();
    setPicker(currentValue => {
      if (currentValue && currentValue.type === e.target.id) {
        return false;
      }
      return { type: e.target.id };
    });
  }, []);

  const onColorPick = useCallback(color => {
    const style = picker.type.includes('bg') ? {
      backgroundColor: color.hex,
    }: {
      border: {
        borderColor: color.hex
      }
    };
    const tableStyle = picker.type.includes('cell') && picker.type.includes('border') ? ({
      border: {
        borderColor: color.hex
      }
    }) : undefined;
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
         op: 'styles',
         entity: picker.type.includes('cell') ? 'cell' : 'column',
         columnUUID: columnUUID,
         style,
         tableStyle,
         key: picker.type.includes('border') ? 'border' : 'backgroundColor'
       },
       itemUUID,
       flowUUID: parentUUID
    }, true);
  }, [itemUUID, parentUUID, picker, columnUUID]);

  const onNameChange = useCallback(
    e => {
      if (regex.NO_SPLCHAR_EXCEPT_HYPHEN.test(e.target.value)) {
        message.error(
          t(
            BLOCK_SETTINGS_OPTIONS.VARIABLE_NAME_SPECIAL_CHARACTER_ERROR_MESSAGE.key,
            BLOCK_SETTINGS_OPTIONS.VARIABLE_NAME_SPECIAL_CHARACTER_ERROR_MESSAGE.value,
          ),
        );
        return;
      } else {
        debouncedUpdateBlockVariableName(e.target.value);
      }
      
    },
    [debouncedUpdateBlockVariableName],
  );

  const onHeaderSwitchChange = useCallback((checked) => {
    methods.updateBlock('tableProperties', itemUUID, {
      tableProperties: {
        showHeader: checked,
      },
      itemUUID,
      flowUUID: parentUUID,
    });
  }, []);

  const onGroupHeaderSwitchChange = useCallback((checked) => {
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
        op: checked ? 'add' : 'delete',
        entity: 'groupHeader',
      },
      itemUUID,
      flowUUID: parentUUID,
    });
  }, []);

  const onTopBlockSwitchChange = useCallback((checked) => {
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
        op: checked ? 'add' : 'delete',
        entity: 'topBlock',
        childBlock: true
      },
      itemUUID,
      flowUUID: parentUUID,
    });
  }, []);

  const onBottomBlockSwitchChange = useCallback((checked) => {
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
        op: checked ? 'add' : 'delete',
        entity: 'bottomBlock',
        childBlock: true
      },
      itemUUID,
      flowUUID: parentUUID,
    });
  }, []);

  const onTopBlockRepeatChange = useCallback((checked) => {
    methods.updateBlock('tableProperties', itemUUID, {
      tableProperties: {
        showtopBlockOnAllPages: checked
      },
      itemUUID,
      flowUUID: parentUUID,
    });
  }, []);

  const onSwitchChange = useCallback((checked) => {
    methods.updateBlock('tableProperties', itemUUID, {
      tableProperties: {
        showHeaderOnAllPages: checked
      },
      itemUUID,
      flowUUID: parentUUID,
    });
  }, []);

  const onEmptyTextChange = useCallback(
    (newContents, newGlobalVariables) => {
      methods.updateBlock('tableProperties', itemUUID, { // TODO : Update new global variables
        tableProperties: {
          emptyText: newContents,
        },
        itemUUID,
        flowUUID: parentUUID,
      });
    },
    [],
  );
  
  const incOrDecPadding = useCallback(async (value, direction) => {
    clearPreviousChanges();
    let convertedValue;
    if(measurement === 'px') {
      convertedValue = value
    } else {
      convertedValue = measurementConversion(value, 'px')
    }
    const cancellablePromise1 = cancellablePromise(new Promise(resolve => setTimeout(resolve, 1000)));
    if (direction !== 'all') {
      headerPaddingChanges.current = Object.assign({}, headerPaddingChanges.current, {
        [direction]: convertedValue
      });
      setLocalHeaderPadding(localPadding => Object.assign({}, localPadding, {
        [direction]: value
      }));
    } else {
      headerPaddingChanges.current = Object.assign({}, headerPaddingChanges.current, {
        top: convertedValue,
        right: convertedValue,
        bottom: convertedValue,
        left: convertedValue,
      });
      setLocalHeaderPadding(localPadding => Object.assign({}, localPadding, {
        top: value,
        right: value,
        bottom: value,
        left: value,
      }));
    }
    changeHeaderPadding(cancellablePromise1);

    try {
      await cancellablePromise1.promise;
      methods.updateBlock('table-operations', itemUUID, {
        'table-operations': {
           op: 'styles',
           entity: 'column',
           style: {
             padding: headerPaddingChanges.current
           }
         },
         itemUUID,
         flowUUID: parentUUID
      }, true);
    } catch (error) {
      removePendingChange(cancellablePromise1);
    }
  }, [itemUUID, parentUUID, measurement]);

  const incOrDecCellPadding = useCallback(async (value, direction) => {
    clearPendingPromises();
    let convertedValue;
    console.log({ value, direction });
    if(measurement === 'px') {
      convertedValue = value
    } else {
      convertedValue = measurementConversion(value, 'px')
    }
    const cancellablePromise1 = cancellablePromise(new Promise(resolve => setTimeout(resolve, 1000)));
    if (direction !== 'all') {
      cellPaddingChanges.current = Object.assign({}, cellPaddingChanges.current, {
        [direction]: convertedValue
      });
      setLocalCellPadding(localPadding => Object.assign({}, localPadding, {
        [direction]: value
      }));
    } else {
      cellPaddingChanges.current = Object.assign({}, cellPaddingChanges.current, {
        top: convertedValue,
        right: convertedValue,
        bottom: convertedValue,
        left: convertedValue,
      });
      setLocalCellPadding(localPadding => Object.assign({}, localPadding, {
        top: value,
        right: value,
        bottom: value,
        left: value,
      }));
    }
    addCancellablePromise(cancellablePromise1);

    try {
      await cancellablePromise1.promise;
      methods.updateBlock('table-operations', itemUUID, {
        'table-operations': {
           op: 'styles',
           entity: 'cell',
           columnUUID: columnUUID,
           style: {
             padding: cellPaddingChanges.current
           }
         },
         itemUUID,
         flowUUID: parentUUID
      }, true);
    } catch (error) {
      removePendingPromise(cancellablePromise1);
    }
  }, [itemUUID, parentUUID, measurement, columnUUID]);

  const onMentionPopupHide = useCallback(() => {
    setPopupPosition(null);
  }, []);

  const toggleMeasurement = useCallback((value) => {
    setMeasurement(value);
    setLocalCellPadding({
      top: value === 'px' ? cellPadding.top : measurementConversion(cellPadding.top, value),
      right: value === 'px' ? cellPadding.right : measurementConversion(cellPadding.right, value),
      bottom: value === 'px' ? cellPadding.bottom : measurementConversion(cellPadding.bottom, value),
      left: value === 'px' ? cellPadding.left : measurementConversion(cellPadding.left, value)
    });
  }, [cellPadding]);

  const onClickAllGrid = useCallback(() => {
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
         op: 'styles',
         entity: 'cell',
         key: 'border',
         columnUUID:columnUUID,
         style: {
           border: {
             left: true,
             right: true,
             top: true,
             bottom: true
           }
         },
         headerStyle: {
           border: {
             left: true,
             right: true,
             top: true,
             bottom: true
           }
         },
         tableStyle: {
           border: {
             left: false,
             right: false,
             top: false,
             bottom: false
           }
         }
       },
       itemUUID,
       flowUUID: parentUUID
    });
  }, [columnUUID]);

  const onClickHorizontalGrid = useCallback(() => {
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
         op: 'styles',
         entity: 'cell',
         key: 'border',
         columnUUID:columnUUID,
         style: {
           border: {
             left: false,
             right: false,
             top: true,
             bottom: true
           }
         },
         headerStyle: {
           border: {
             left: false,
             right: false,
             top: true,
             bottom: true
           }
         },
         tableStyle: {
           border: {
             left: false,
             right: false,
             top: false,
             bottom: false
           }
         }
       },
       itemUUID,
       flowUUID: parentUUID
    })
  }, [columnUUID]);

  const onColumnVariableMap = useCallback(variable => {
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
        op: 'repeat',
        entity: 'column',
        key: 'repeat',
        columnUUID: columnUUID,
        content: variable.placeHolderText === variable.variableCategoryName ? null : variable.placeHolderText
      },
      flowUUID: parentUUID
    });
    repeatVariableRef.current.blur();
    previousRepeatValueRef.current = variable.placeHolderText;
    setPopupPosition(null);
  }, [columnUUID, parentUUID]);

  const onClickVerticalGrid = useCallback(() => {
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
         op: 'styles',
         entity: 'cell',
         key: 'border',
         columnUUID:columnUUID,
         style: {
           border: {
             left: false,
             right: false,
             top: false,
             bottom: false
           }
         },
         headerStyle: {
           border: {
             left: true,
             right: true,
             top: true,
             bottom: true
           }
         },
         tableStyle: {
           border: {
             left: true,
             right: true,
             top: true,
             bottom: true,
             borderColor: dynamicTableBlock.contents[0]?.content?.style?.border?.borderColor
           }
         }
       },
       itemUUID,
       flowUUID: parentUUID
    })
  }, [columnUUID]);

  const applyAllSides = Object.keys(headerPadding).length > 0 ? (headerPadding.top === headerPadding.bottom &&
      headerPadding.top === headerPadding.right &&
      headerPadding.top === headerPadding.left) : false;
  const header = dynamicTableBlock.contents[0]?.headerContent;
  const cell = Object.assign({}, dynamicTableBlock.contents[selectedColumn || 0]?.content);
  const cellDetails = Object.assign({}, dynamicTableBlock.contents[selectedColumn || 0]);

  const applyAllSidesForCell = Object.keys(cellPadding).length > 0 ? (cellPadding.top === cellPadding.bottom &&
    cellPadding.top === cellPadding.right &&
    cellPadding.top === cellPadding.left) : false;

  const cellBorder = cell?.style?.border
  const allSidesGrid = cellBorder ? cellBorder.top && cellBorder.right && cellBorder.bottom && cellBorder.left : false;
  const horizontalGrid = cellBorder ? cellBorder.top && !cellBorder.right && cellBorder.bottom && !cellBorder.left : false;
  const verticalGrid = cellBorder ? !cellBorder.top && !cellBorder.right && !cellBorder.bottom && !cellBorder.left : false;

  const linearGrid = dynamicTableBlock.variableContract[0]?.content?.find(contract => contract.type === 'object') === undefined;
  const nest2Grid = linearGrid
    ? false
    : dynamicTableBlock.variableContract[0]?.content?.filter(contract => contract.type === 'object')[0]?.content?.find(contract => contract.type === 'object') !== undefined;

  const selectedColumnName= (selectedColumn ?? null) !== null ? `column ${selectedColumn + 1}` : 'all columns';
  const tableDataContract = dynamicTableBlock.variableContract[0]?.isArray
      ? dynamicTableBlock.variableContract[0]?.content?.find(variable => variable.type === 'object')
      : dynamicTableBlock.variableContract[0];
  const tableName = (tableDataContract?.name) || '';
  let foundSubTable = false;
  const tableVariables = tableDataContract?.content?.reduce((acc, contract) => {
    let localVariablePrefix = `${tableName}.`;
    if (!foundSubTable && contract.type === 'object') {
      let localContract = contract;
      if (foundSubTable) return acc;
      while (localContract?.type === 'object') {
        if (!foundSubTable) {
          foundSubTable = localContract?.type === 'object' && localContract.isArray === true;
        }
        acc = [...acc, {
          placeHolderText: `${localVariablePrefix}${localContract.name}`,
          variableCategoryId : 0,
          variableCategoryName: tableName
        }];
        localVariablePrefix += `${localContract.name}.`;
        localContract = localContract.content.find(nestedContract => nestedContract.type === 'object');
      }
    }
    return acc;
  }, [{ placeHolderText: tableName, variableCategoryId: 0, variableCategoryName: tableName }]);

  return (
    <Container className={'full-height'}>
      <div style={{ width: '100%', height: '100%', overflow: 'auto' }} className={'scrollable'}>
        <FieldTitle style={{ fontSize: '16px', padding: '10px 0 15px 20px' }}>
          {t(DYNAMIC_TABLE_SETTINGS_SIDEBAR.DYNAMIC_TABLE_SETTINGS_TITLE.key, DYNAMIC_TABLE_SETTINGS_SIDEBAR.DYNAMIC_TABLE_SETTINGS_TITLE.value)}
        </FieldTitle>
        {selectedColumn === null && <ColumnSection>
          <FieldTitle>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.DYNAMIC_TABLE_VARIABLE_NAME.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.DYNAMIC_TABLE_VARIABLE_NAME.value,
            )}
          </FieldTitle>
          <Input
            ref={fieldNameRef} // TODO : refresh on change of current block
            defaultValue={tableName}
            disabled={disableVariableName}
            placeholder={t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.DYNAMIC_TABLE_VARIABLE_NAME.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.DYNAMIC_TABLE_VARIABLE_NAME.value,
            )}
            onChange={onNameChange}
          />
        </ColumnSection>}

        {selectedColumn === null && <>
        <FieldTitle isStyleChange={1} style={{ paddingLeft: 20, paddingRight: 20 }}>
          {t(
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_HEADER_SETTING.key,
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_HEADER_SETTING.value,
          )}
          <Tooltip
            title={t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_HEADER_SETTING.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_HEADER_SETTING.value,
            )}
          >
            <Switch
              checked={dynamicTableBlock.style.tableProperties?.showHeader}
              onChange={onHeaderSwitchChange}
            />
          </Tooltip>
        </FieldTitle>
        <FieldTitle isStyleChange={1} style={{ paddingLeft: 20, paddingRight: 20 }}>
          {t(
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_GROUP_HEADER_SETTING.key,
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_GROUP_HEADER_SETTING.value,
          )}
          <Tooltip
            title={t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_GROUP_HEADER_SETTING.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_GROUP_HEADER_SETTING.value,
            )}
          >
            <Switch
              checked={dynamicTableBlock.style.tableProperties?.showGroupHeaderContent}
              onChange={onGroupHeaderSwitchChange}
            />
          </Tooltip>
        </FieldTitle>
        {!isEmailTemplate && <FieldTitle isStyleChange={1} style={{ paddingLeft: 20, paddingRight: 20 }}>
          {t(
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_HEADER_IN_ALL_PAGES.key,
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_HEADER_IN_ALL_PAGES.value,
          )}
          <Tooltip
            title={t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_HEADER_IN_ALL_PAGES_TOOLTIP.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_HEADER_IN_ALL_PAGES_TOOLTIP.value,
            )}
          >
            <Switch
              checked={dynamicTableBlock.style.tableProperties?.showHeaderOnAllPages}
              onChange={onSwitchChange}
              disabled={!dynamicTableBlock.style.tableProperties?.showHeader
                && !dynamicTableBlock.style.tableProperties?.showGroupHeaderContent}
            />
          </Tooltip>
        </FieldTitle>}
        <FieldTitle isStyleChange={1} style={{ paddingLeft: 20, paddingRight: 20 }}>
          {t(
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_TITLE_SETTINGS.key,
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_TITLE_SETTINGS.value,
          )}
          <Tooltip
            title={t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_TITLE_SETTINGS.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_TITLE_SETTINGS.value,
            )}
          >
            <Switch
              checked={dynamicTableBlock.style.tableProperties?.showTopBlock}
              onChange={onTopBlockSwitchChange}
            />
          </Tooltip>
        </FieldTitle>
        {!isEmailTemplate && dynamicTableBlock.style.tableProperties?.showTopBlock && (<FieldTitle isStyleChange={1} style={{ paddingLeft: 20, paddingRight: 20 }}>
          {t(
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_TITLE_IN_ALL_PAGES.key,
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_TITLE_IN_ALL_PAGES.value,
          )}
          <Tooltip
            title={t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_TITLE_IN_ALL_PAGES_TOOLTIP.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_TITLE_IN_ALL_PAGES_TOOLTIP.value,
            )}
          >
            <Switch
              checked={dynamicTableBlock.style.tableProperties?.showtopBlockOnAllPages}
              onChange={onTopBlockRepeatChange}
            />
          </Tooltip>
        </FieldTitle>)}
        <FieldTitle isStyleChange={1} style={{ paddingLeft: 20, paddingRight: 20 }}>
          {t(
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_FOOTER_SETTINGS.key,
            DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_FOOTER_SETTINGS.value,
          )}
          <Tooltip
            title={t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_FOOTER_SETTINGS.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SHOW_FOOTER_SETTINGS.value,
            )}
          >
            <Switch
              checked={dynamicTableBlock.style.tableProperties?.showBottomBlock}
              onChange={onBottomBlockSwitchChange}
            />
          </Tooltip>
        </FieldTitle>
        <ColumnSection>
          <FieldTitle isStyleChange={1}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.EMPTY_TEXT_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.EMPTY_TEXT_LABEL.value,
            )}
          </FieldTitle>
          <BorderedWhiteQuillRenderer
            uuid={itemUUID}
            contents={dynamicTableBlock?.style?.tableProperties?.emptyText}
            globalVariables={globalVariables}
            updateContent={onEmptyTextChange}
            canUpdateRandomKey={canUpdateRandomKey}
          />
        </ColumnSection>
        <ColumnSection>
          <FieldTitle isStyleChange={1} className={'section-header'}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.GRID_STYLING_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.GRID_STYLING_LABEL.value,
              {info: selectedColumnName}
            )}
          </FieldTitle>
          <RowSection style={{ justifyContent: 'space-between' }}>
            <AllSidesGrid selected={allSidesGrid} onClick={onClickAllGrid} />
            <HorizontalBorderGrid selected={horizontalGrid} onClick={onClickHorizontalGrid} />
            <VerticalBorderGrid selected={verticalGrid} onClick={onClickVerticalGrid} />
          </RowSection>
        </ColumnSection>
        {dynamicTableBlock.style.tableProperties.showHeader && <>
        <ColumnSection>
          <FieldTitle isStyleChange={1} className={'section-header'}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.HEADER_STYLING_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.HEADER_STYLING_LABEL.value,
            )}
          </FieldTitle>
        <RowSection>
          <FieldTitle isStyleChange={1} style={{ textTransform: 'none' }}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SPACING_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SPACING_LABEL.value,
            )}
          </FieldTitle>
          <Select value={measurement} style={{ width: 75 }} onChange={toggleMeasurement} disabled={isEmailTemplate}>
            <Option value="mm">mm</Option>
            <Option value="px">px</Option>
          </Select>
        </RowSection>
        <PaddingOption style={{ justifyContent: 'space-between', paddingRight: '8%' }}>
          <FieldTitle isSpacing={1}>
            {t(
              BLOCK_SETTINGS_OPTIONS.ALL_SIDES_SPACING_LABEL.key,
              BLOCK_SETTINGS_OPTIONS.ALL_SIDES_SPACING_LABEL.value,
            )}
            <Switch checked={applyAllSides} />
          </FieldTitle>
          <InputNumber
            min={0}
            value={
               applyAllSides
                ? localHeaderPadding.top
                : undefined
            }
            onChange={e => incOrDecPadding(e, 'all')}
            // disabled={!editable}
          />
        </PaddingOption>
        <RowSection>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.TOP_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.TOP_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={localHeaderPadding.top}
            onChange={e => incOrDecPadding(e, 'top')}
            // disabled={!editable}
          />
        </PaddingOption>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.BOTTOM_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.BOTTOM_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={localHeaderPadding.bottom}
            onChange={e => incOrDecPadding(e, 'bottom')}
            // disabled={!editable}
          />
        </PaddingOption>
        </RowSection>
        <RowSection>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.RIGHT_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.RIGHT_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={localHeaderPadding.right}
            onChange={e => incOrDecPadding(e, 'right')}
            // disabled={!editable}
          />
        </PaddingOption>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.LEFT_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.LEFT_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={localHeaderPadding.left}
            onChange={e => incOrDecPadding(e, 'left')}
            // disabled={!editable}
          />
        </PaddingOption>
        </RowSection>
        </ColumnSection>
        </>}
        <ColumnSection>
          <RowSection>
            <FieldTitle isStyleChange={1} style={{ textTransform: 'none' }}>
              {t(
                DYNAMIC_TABLE_SETTINGS_SIDEBAR.BACKGROUND_LABEL.key,
                DYNAMIC_TABLE_SETTINGS_SIDEBAR.BACKGROUND_LABEL.value,
              )}
            </FieldTitle>
            <BlockColor
              id={'header-bg'}
              background={header?.style?.backgroundColor}
              onClick={showBackgroundColorPicker}
              // disabled={!editable}
            />
            {picker && picker.type === 'header-bg' && (
              <ColorPickerContainer>
                <ColorPicker
                  onChange={onColorPick}
                  onBlur={() => setPicker(false)}
                  color={header?.style?.backgroundColor}
                  qlEditor={true}
                />
              </ColorPickerContainer>
            )}
          </RowSection>
        </ColumnSection>
        <ColumnSection>
          <RowSection>
            <FieldTitle isStyleChange={1} style={{ textTransform: 'none' }}>
              {t(
                DYNAMIC_TABLE_SETTINGS_SIDEBAR.BORDER_LABEL.key,
                DYNAMIC_TABLE_SETTINGS_SIDEBAR.BORDER_LABEL.value,
              )}
            </FieldTitle>
            <BlockColor
              id={'header-border'}
              background={header?.style?.border?.borderColor}
              onClick={showBackgroundColorPicker}
              // disabled={!editable}
            />
            {picker && picker.type === 'header-border' && (
              <ColorPickerContainer>
                <ColorPicker
                  onChange={onColorPick}
                  onBlur={() => setPicker(false)}
                  color={header?.style?.border?.borderColor}
                  qlEditor={true}
                />
              </ColorPickerContainer>
            )}
          </RowSection>
        </ColumnSection>
        </>}
          <FieldTitle isStyleChange={1} className={'section-header'} style={{ marginLeft: 20 }}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.CELL_STYLING_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.CELL_STYLING_LABEL.value,
              {info : selectedColumnName}
            )}
          </FieldTitle>
        {cell?.contents.length > 0 ? (
        <>
        {selectedColumn !== null && <ColumnSection>
          <FieldTitle>
            {/*t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.DYNAMIC_TABLE_VARIABLE_NAME.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.DYNAMIC_TABLE_VARIABLE_NAME.value,
            )*/}
            {'Repeat rows on variable'}
          </FieldTitle>
          <Input
            ref={repeatVariableRef}
            /*defaultValue={cellDetails?.repeatVariable
              ? cellDetails?.repeatVariable
              : dynamicTableBlock.variableContract[0]?.name}
            placeholder={t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.DYNAMIC_TABLE_VARIABLE_NAME.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.DYNAMIC_TABLE_VARIABLE_NAME.value,
            )}
            onChange={onColumnNameChange}*/
              onChange={event => {
                setSearchTerm(event.target.value);
                setPopupPosition({
                  left: event.target.offsetLeft,
                  bottom: event.target.offsetTop + event.target.offsetHeight,
                  target: event.target,
                  parentElement: event.target.parentElement.parentElement
                });
              }}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                previousRepeatValueRef.current = e.target.value;
                setSearchTerm(e.target.value);
                setPopupPosition({
                  left: e.target.offsetLeft,
                  bottom: e.target.offsetTop + e.target.offsetHeight,
                  target: e.target,
                  parentElement: e.target.parentElement.parentElement
                });
              }}
              placeHolder={"Select variable"}
              value={searchTerm}
              disabled
              onBlur={() => {
                setPopupPosition(null);
                setSearchTerm(value => {
                  if (value === '') {
                    return previousRepeatValueRef.current;
                  }
                  return value;
                })
              }}
          />
          {popupPosition && (
            <VariableMentionPopup
              uuid={selectedColumn}
              searchTerm={searchTerm}
              onVariableSelect={(variable, allVariables) => {
                onColumnVariableMap(variable);
              }}
              onHide={onMentionPopupHide}
              position={popupPosition}
              popupPlacement={"bottom"}
              target={popupPosition.target}
              globalVariables={tableVariables}
              updateGlobalVariables={dummyCallback}
              onlyLocal={true}
            />
          )}
        </ColumnSection>}
        <ColumnSection>
        <RowSection>
          <FieldTitle isStyleChange={1} style={{ textTransform: 'none' }}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SPACING_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.SPACING_LABEL.value,
            )}
          </FieldTitle>
          <Select value={measurement} style={{ width: 75 }} onChange={toggleMeasurement} disabled={isEmailTemplate}>
            <Option value="mm">mm</Option>
            <Option value="px">px</Option>
          </Select>
        </RowSection>
        <PaddingOption style={{ justifyContent: 'space-between', paddingRight: '8%' }}>
          <FieldTitle isSpacing={1}>
            {t(
              BLOCK_SETTINGS_OPTIONS.ALL_SIDES_SPACING_LABEL.key,
              BLOCK_SETTINGS_OPTIONS.ALL_SIDES_SPACING_LABEL.value,
            )}
            <Switch checked={applyAllSidesForCell} />
          </FieldTitle>
          <InputNumber
            min={0}
            value={
               applyAllSidesForCell
                ? localCellPadding.top
                : undefined
            }
            onChange={e => incOrDecCellPadding(e, 'all')}
            // disabled={!editable}
          />
        </PaddingOption>
        <RowSection>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.TOP_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.TOP_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={localCellPadding.top}
            onChange={e => incOrDecCellPadding(e, 'top')}
            // disabled={!editable}
          />
        </PaddingOption>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.BOTTOM_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.BOTTOM_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={localCellPadding.bottom}
            onChange={e => incOrDecCellPadding(e, 'bottom')}
            // disabled={!editable}
          />
        </PaddingOption>
        </RowSection>
        <RowSection>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.RIGHT_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.RIGHT_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={localCellPadding.right}
            onChange={e => incOrDecCellPadding(e, 'right')}
            // disabled={!editable}
          />
        </PaddingOption>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.LEFT_LABEL.key,
              DYNAMIC_TABLE_SETTINGS_SIDEBAR.LEFT_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={localCellPadding.left}
            onChange={e => incOrDecCellPadding(e, 'left')}
            // disabled={!editable}
          />
        </PaddingOption>
        </RowSection>
        </ColumnSection>
        <ColumnSection>
          <RowSection>
            <FieldTitle isStyleChange={1} style={{ textTransform: 'none' }}>
              {t(
                DYNAMIC_TABLE_SETTINGS_SIDEBAR.BACKGROUND_LABEL.key,
                DYNAMIC_TABLE_SETTINGS_SIDEBAR.BACKGROUND_LABEL.value,
              )}
            </FieldTitle>
            <BlockColor
              id={'cell-bg'}
              background={cell?.style?.backgroundColor}
              onClick={showBackgroundColorPicker}
              // disabled={!editable}
            />
            {picker && picker.type === 'cell-bg' && (
              <ColorPickerContainer>
                <ColorPicker
                  onChange={onColorPick}
                  onBlur={() => setPicker(false)}
                  color={header?.style?.backgroundColor}
                  qlEditor={true}
                />
              </ColorPickerContainer>
            )}
          </RowSection>
        </ColumnSection>
        <ColumnSection>
          <RowSection>
            <FieldTitle isStyleChange={1} style={{ textTransform: 'none' }}>
              {t(
                DYNAMIC_TABLE_SETTINGS_SIDEBAR.BORDER_LABEL.key,
                DYNAMIC_TABLE_SETTINGS_SIDEBAR.BORDER_LABEL.value,
              )}
            </FieldTitle>
            <BlockColor
              id={'cell-border'}
              background={cell?.style?.border?.borderColor}
              onClick={showBackgroundColorPicker}
              // disabled={!editable}
            />
            {picker && picker.type === 'cell-border' && (
              <ColorPickerContainer>
                <ColorPicker
                  onChange={onColorPick}
                  onBlur={() => setPicker(false)}
                  color={cell?.style?.border?.borderColor}
                  qlEditor={true}
                />
              </ColorPickerContainer>
            )}
          </RowSection>
        </ColumnSection>
        </>) : <EmptyMappingNote>{t(DYNAMIC_TABLE_SETTINGS_SIDEBAR.EMPTY_MAPPING_NOTE.key, DYNAMIC_TABLE_SETTINGS_SIDEBAR.EMPTY_MAPPING_NOTE.value)}</EmptyMappingNote>}
      </div>
      {ReactDOM.createPortal(
        <div
          className="layoutbtn-container"
          onClick={onClose}
          style={{ position: 'absolute', top: 0, left: -2 }}
        >
          <Icon type={'close'} />
        </div>,
        document.getElementById('sidebar-absolute-close'),
      )}
    </Container>
  )
}

export default DynamicTableSettings;